import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useSessionTimeout } from '../hooks/useSessionTimeout';
import PixelCorgi from './PixelCorgi';
import ComplimentGenerator from './ComplimentGenerator';
import corgiLove from '/corgilove.png';

const MOODS = [
  { emoji: '😡', label: 'Angry' },
  { emoji: '😢', label: 'Sad' },
  { emoji: '😐', label: 'Meh' },
  { emoji: '😊', label: 'Happy' },
  { emoji: '🥰', label: 'In Love' }
];

const LOVE_COOLDOWN = 300; // seconds

const MessageForm = () => {
  useSessionTimeout();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [selectedMood, setSelectedMood] = useState(2);
  const [message, setMessage] = useState('');
  const [loveCooldown, setLoveCooldown] = useState(0);
  const [isLoveButtonDisabled, setIsLoveButtonDisabled] = useState(false);
  const [selectedForm, setSelectedForm] = useState(null);
  const [showSuccess, setShowSuccess] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const navigate = useNavigate();

  // Countdown state with added animation flags
  const [nextMeetDate, setNextMeetDate] = useState(() => {
    const saved = localStorage.getItem('nextMeetDate');
    return saved ? new Date(saved) : null;
  });
  const [countdown, setCountdown] = useState('');
  const [isEditingDate, setIsEditingDate] = useState(false);
  const [tempDate, setTempDate] = useState('');
  const [isCountdownHovered, setIsCountdownHovered] = useState(false);

  // Calculate countdown
  useEffect(() => {
    if (!nextMeetDate) return;

    const timer = setInterval(() => {
      const now = new Date();
      const difference = nextMeetDate - now;

      if (difference <= 0) {
        setCountdown('It\'s time! 🎉');
        return;
      }

      const days = Math.floor(difference / (1000 * 60 * 60 * 24));
      const hours = Math.floor((difference % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
      const minutes = Math.floor((difference % (1000 * 60 * 60)) / (1000 * 60));
      const seconds = Math.floor((difference % (1000 * 60)) / 1000);

      setCountdown(`${days}d ${hours}h ${minutes}m ${seconds}s`);
    }, 1000);

    return () => clearInterval(timer);
  }, [nextMeetDate]);

  // Enhanced date update handler
  const handleDateUpdate = () => {
    const newDate = new Date(tempDate);
    if (!isNaN(newDate.getTime())) {
      setNextMeetDate(newDate);
      localStorage.setItem('nextMeetDate', newDate.toISOString());
      setIsEditingDate(false);
    }
  };

  // Format date for display
  const formatDateForDisplay = (date) => {
    if (!date) return '';
    return new Date(date).toLocaleDateString('en-US', {
      weekday: 'long',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  // Reset scroll position when component mounts
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  useEffect(() => {
    const cooldownStr = localStorage.getItem('loveCooldown');
    if (cooldownStr) {
      const cooldownTime = parseInt(cooldownStr);
      if (cooldownTime > Date.now()) {
        setIsLoveButtonDisabled(true);
        setLoveCooldown(Math.ceil((cooldownTime - Date.now()) / 1000));
      }
    }
  }, []);

  useEffect(() => {
    let interval;
    if (loveCooldown > 0) {
      interval = setInterval(() => {
        setLoveCooldown(prev => {
          if (prev <= 1) {
            setIsLoveButtonDisabled(false);
            return 0;
          }
          return prev - 1;
        });
      }, 1000);
    }
    return () => clearInterval(interval);
  }, [loveCooldown]);

  const sendMessage = async (messageData) => {
    try {
      setIsSubmitting(true);
      setErrorMessage('');
      console.log('Sending message:', messageData);

      const response = await fetch('/.netlify/functions/send-message', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(messageData)
      });

      console.log('Response status:', response.status);

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to send message');
      }

      setShowSuccess(true);
      setTimeout(() => setShowSuccess(false), 3000);
      setMessage('');
      setSelectedMood(2);
      setSelectedForm(null);
    } catch (error) {
      console.error('Error sending message:', error);
      setErrorMessage(error.message || 'Failed to send message. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleLoveClick = async () => {
    setIsLoveButtonDisabled(true);
    const cooldownTime = Date.now() + (LOVE_COOLDOWN * 1000);
    localStorage.setItem('loveCooldown', cooldownTime.toString());
    setLoveCooldown(LOVE_COOLDOWN);

    await sendMessage({
      message: '💖 Quick Love! 💖',
      type: 'quick-love'
    });
  };

  const handleLogout = () => {
    localStorage.removeItem('isAuthenticated');
    navigate('/');
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    const formData = {
      mood: MOODS[selectedMood].emoji,
      message: `${MOODS[selectedMood].emoji} ${message}`,
      type: selectedForm
    };

    await sendMessage(formData);
  };

  const renderForm = () => {
    if (!selectedForm) return null;

    return (
      <div className="bg-white/80 backdrop-blur-lg rounded-2xl shadow-xl p-8 animate-fade-in mt-6" style={{ animationDelay: '0.4s' }}>
        {/* Mood Selector */}
        <div className="mb-8">
          <label className="block text-2xl font-bold bg-clip-text text-transparent bg-gradient-to-r from-purple-600 via-pink-500 to-purple-600 mb-4">
            nay mood?
          </label>
          <div className="flex justify-between items-center bg-transparent p-4 rounded-xl">
            {MOODS.map((mood, index) => (
              <button
                key={mood.label}
                onClick={() => setSelectedMood(index)}
                className={`text-4xl transition-all duration-300 hover:scale-110 p-2 rounded-full ${selectedMood === index
                  ? 'scale-125 transform-gpu'
                  : 'opacity-70 hover:opacity-100'
                  }`}
                style={{ background: 'none' }}
              >
                {mood.emoji}
              </button>
            ))}
          </div>
        </div>

        {/* Message Form */}
        <form onSubmit={onSubmit} className="space-y-6">
          <div>
            <label className="block text-2xl font-bold bg-clip-text text-transparent bg-gradient-to-r from-purple-600 via-pink-500 to-purple-600 mb-2">
              {selectedForm === 'request' ? 'Request Ryan' : 'Message Ryan'}
            </label>
            <textarea
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              className="w-full p-4 border border-gray-200 rounded-xl focus:ring-2 focus:ring-purple-500 focus:border-transparent bg-white/50 backdrop-blur-sm transition-all duration-200 hover:bg-white/80"
              rows="4"
              placeholder={selectedForm === 'request' ? "what are u in need of?" : "hi nay whats up"}
              required
            />
            <div className="text-right text-sm text-gray-500 mt-2">
              {message.length} characters
            </div>
          </div>

          <button
            type="submit"
            disabled={isSubmitting}
            className="w-full py-3 bg-gradient-to-r from-purple-500 to-pink-500 text-white rounded-lg font-medium hover:opacity-90"
          >
            {isSubmitting ? 'Sending...' : 'Send ✨'}
          </button>
        </form>
      </div>
    );
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-pink-200 via-purple-200 to-blue-200 relative overflow-x-hidden">
      {/* Animated background elements */}
      <div className="fixed inset-0 overflow-hidden">
        <div className="absolute -top-1/2 -left-1/2 w-full h-full bg-gradient-to-br from-pink-300/30 to-purple-300/30 rounded-full blur-3xl animate-float"></div>
        <div className="absolute -bottom-1/2 -right-1/2 w-full h-full bg-gradient-to-br from-blue-300/30 to-purple-300/30 rounded-full blur-3xl animate-float" style={{ animationDelay: '2s' }}></div>
      </div>

      {/* Success Screen */}
      {showSuccess && (
        <div className="fixed inset-0 flex items-center justify-center z-50 bg-black/30 backdrop-blur-sm">
          <div className="relative">
            <img
              src={corgiLove}
              alt="Corgi Love"
              className="w-64 h-64 object-contain animate-bounce"
              onError={(e) => console.error('Image failed to load:', e)}
            />
            <div className="absolute -top-4 -left-4 text-6xl animate-float delay-100">✨</div>
            <div className="absolute -bottom-4 -right-4 text-6xl animate-float delay-300">✨</div>
            <div className="mt-4 text-2xl font-bold text-white text-center animate-fade-in">
              Sent with love! 💌
            </div>
          </div>
        </div>
      )}

      {/* Cute bouncing corgis - adjusted for better mobile positioning */}
      <PixelCorgi variant={1} startPosition={{ x: '5vw', y: '5vh' }} />
      <PixelCorgi variant={2} startPosition={{ x: '85vw', y: '25vh' }} />
      <PixelCorgi variant={3} startPosition={{ x: '45vw', y: '55vh' }} />
      <PixelCorgi variant={4} startPosition={{ x: '15vw', y: '40vh' }} />
      <PixelCorgi variant={5} startPosition={{ x: '75vw', y: '10vh' }} />

      <div className="max-w-2xl mx-auto relative px-4 py-6 sm:px-6 sm:py-8">
        {/* Enhanced Header */}
        <div className="flex flex-col items-center w-full mb-8 sm:mb-12">
          <h1 className="text-4xl sm:text-6xl font-bold bg-clip-text text-transparent relative animate-fade-in leading-normal
                       bg-gradient-to-r from-purple-600 via-pink-500 to-purple-600 animate-shimmer
                       bg-[length:200%_auto] bg-[size:200%_auto] mb-6">
            Naomi Portal
          </h1>

          {/* Responsive container for Next Time Together and ComplimentGenerator */}
          <div className="w-full flex flex-col sm:flex-row gap-4 justify-center items-stretch">
            {/* Next Time Together Box */}
            <div className="flex-1 min-w-0">
              <div
                className="bg-white/90 backdrop-blur-sm rounded-3xl p-6 shadow-lg mb-0 w-full h-full max-w-md mx-auto transform transition-all duration-300 hover:shadow-xl"
                onMouseEnter={() => setIsCountdownHovered(true)}
                onMouseLeave={() => setIsCountdownHovered(false)}
              >
                <div className="text-center">
                  <h2 className="text-2xl sm:text-3xl font-bold text-transparent bg-clip-text bg-gradient-to-r from-blue-400 via-indigo-500 to-blue-600 mb-3">
                    Next Time Together
                  </h2>

                  {isEditingDate ? (
                    <div className="space-y-4">
                      {/* Modern Date Input */}
                      <div className="relative">
                        <input
                          type="datetime-local"
                          value={tempDate}
                          onChange={(e) => setTempDate(e.target.value)}
                          className="w-full px-4 py-3 rounded-xl border-2 border-purple-200 focus:ring-2 focus:ring-purple-500 focus:border-transparent bg-white/50 backdrop-blur-sm transition-all duration-200 text-purple-700 font-medium"
                        />
                      </div>

                      {/* Action Buttons */}
                      <div className="flex gap-2 justify-center">
                        <button
                          onClick={handleDateUpdate}
                          className="px-6 py-2.5 bg-gradient-to-r from-purple-500 to-pink-500 text-white rounded-xl font-medium hover:opacity-90 transform transition-all duration-200 hover:scale-105 shadow-md hover:shadow-lg"
                        >
                          Save Date ✨
                        </button>
                        <button
                          onClick={() => setIsEditingDate(false)}
                          className="px-6 py-2.5 bg-gray-100 text-gray-700 rounded-xl font-medium hover:bg-gray-200 transform transition-all duration-200"
                        >
                          Cancel
                        </button>
                      </div>
                    </div>
                  ) : (
                    <div className="space-y-3">
                      {/* Countdown Display */}
                      <div className="text-3xl font-bold text-purple-700 transition-all duration-300">
                        {nextMeetDate ? countdown : 'Set a date! ✨'}
                      </div>

                      {nextMeetDate && (
                        <div className="text-sm text-purple-500 font-medium opacity-80">
                          {formatDateForDisplay(nextMeetDate)}
                        </div>
                      )}

                      {/* Edit Button */}
                      <button
                        onClick={() => {
                          setIsEditingDate(true);
                          setTempDate(nextMeetDate ? nextMeetDate.toISOString().slice(0, 16) : '');
                        }}
                        className={`mt-2 px-4 py-2 rounded-xl font-medium transition-all duration-300 ${isCountdownHovered
                          ? 'bg-gradient-to-r from-purple-500 to-pink-500 text-white transform scale-105'
                          : 'bg-purple-100 text-purple-700'
                          }`}
                      >
                        {nextMeetDate ? 'Update Date 📅' : 'Set Date ✨'}
                      </button>
                    </div>
                  )}
                </div>
              </div>
            </div>
            {/* Compliment Generator Box */}
            <div className="flex-1 min-w-0 flex items-stretch">
              <ComplimentGenerator />
            </div>
          </div>

          {/* Logout Button */}
          <div className="absolute right-4 top-4">
            <button
              onClick={handleLogout}
              className="px-4 sm:px-6 py-2 bg-white/80 hover:bg-white shadow-md rounded-full text-gray-700 hover:text-gray-900 transition-all duration-200 backdrop-blur-sm border border-gray-100 text-sm sm:text-base"
            >
              Logout
            </button>
          </div>
        </div>

        {/* Error Message */}
        {errorMessage && (
          <div className="mb-4 p-4 bg-red-100 border border-red-400 text-red-700 rounded-lg">
            {errorMessage}
          </div>
        )}

        {/* Main Buttons */}
        <div className="grid grid-cols-3 gap-2 sm:gap-4">
          <button
            onClick={() => setSelectedForm('message')}
            disabled={isSubmitting}
            className={`p-4 sm:p-6 rounded-xl font-bold text-white transition-all duration-300 transform hover:scale-105 
                     bg-gradient-to-r from-purple-500 to-pink-500 shadow-lg hover:shadow-xl text-sm sm:text-base
                     ${selectedForm === 'message' ? 'scale-105 ring-4 ring-purple-300' : ''}
                     ${isSubmitting ? 'opacity-50 cursor-not-allowed' : ''}`}
          >
            {isSubmitting ? 'Sending...' : 'Send Message'}
          </button>
          <button
            onClick={() => setSelectedForm('request')}
            disabled={isSubmitting}
            className={`p-4 sm:p-6 rounded-xl font-bold text-white transition-all duration-300 transform hover:scale-105 
                     bg-gradient-to-r from-blue-500 to-purple-500 shadow-lg hover:shadow-xl text-sm sm:text-base
                     ${selectedForm === 'request' ? 'scale-105 ring-4 ring-purple-300' : ''}
                     ${isSubmitting ? 'opacity-50 cursor-not-allowed' : ''}`}
          >
            {isSubmitting ? 'Sending...' : 'Send Request'}
          </button>
          <button
            onClick={() => {
              setSelectedForm('love');
              handleLoveClick();
            }}
            disabled={isLoveButtonDisabled || isSubmitting}
            className={`p-4 sm:p-6 rounded-xl font-bold text-white transition-all duration-300 transform hover:scale-105 
                     bg-gradient-to-r from-red-400 to-pink-400 shadow-lg hover:shadow-xl text-sm sm:text-base
                     ${isLoveButtonDisabled || isSubmitting ? 'opacity-50 cursor-not-allowed' : ''}
                     ${selectedForm === 'love' ? 'scale-105 ring-4 ring-pink-300' : ''}`}
          >
            {isSubmitting ? 'Sending...' : 'Quick Love 💝'}
            {loveCooldown > 0 && (
              <div className="text-xs sm:text-sm mt-1 sm:mt-2 opacity-90">
                {loveCooldown}s
              </div>
            )}
          </button>
        </div>

        {/* Render the selected form */}
        {renderForm()}
      </div>
    </div>
  );
};

export default MessageForm;